"use client";
import { useMemo } from "react";
import Link from "next/link";
import { SingleProductDescriptionFocused } from "@/components/singleProduct/singleProductDescriptionFocused";
import { SingleProductImageFocused } from "@/components/singleProduct/singleProductImageFocused";
import { applyDataTestId } from "@/utils/testUtils";
import pom from "@/utils/pom";
import clsx from "clsx";
import { Item } from "@/dto/item";
import { generateItemUrl } from "@/utils/itemUrlUtils";
import { SessionStorageKey } from "@/utils/storageKeys";
import { saveToSessionStorage } from "@/utils/sessionStorageUtils";
import { AppEventEmitter } from "@/components/appEventEmitter/appEventEmitter";

interface ProcessedItem {
  item: Item;
  url: string;
}

export const CategoryItems = ({
  category,
  merchantName,
  itemTemplate,
  itemsMap,
  query,
  getItemHref,
  baseRoute,
  queryParams,
}: any) => {
  const validItems = useMemo<ProcessedItem[]>(() => {
    const normalizedQuery = query?.toLowerCase() ?? "";

    return category.items.reduce((acc: ProcessedItem[], itemId: Item["id"]) => {
      const item =
        category.id !== "Uncategorized" ? itemsMap?.[itemId] : itemId;

      if (!item?.name?.toLowerCase().includes(normalizedQuery)) {
        return acc;
      }

      let url = "";
      if (getItemHref) {
        url = getItemHref(item);
      } else if (baseRoute) {
        url = generateItemUrl({
          item,
          baseRoute,
          queryParams: queryParams ?? "",
        });
      }

      acc.push({ item, url });

      return acc;
    }, []);
  }, [
    category.items,
    category.id,
    itemsMap,
    query,
    getItemHref,
    baseRoute,
    queryParams,
  ]);

  if (validItems.length === 0) {
    return null;
  }

  const headerId = `${category.name.replaceAll(/\s+/g, "-")}-header`;

  return (
    <div key={category.id} className="category" id={category.name}>
      <AppEventEmitter />
      <h2
        id={headerId}
        tabIndex={-1}
        className="mt-8 mb-5 font-medium text-[15px] text-[#5C5C5C]"
      >
        {String(category.name).toUpperCase()}
      </h2>

      <ol
        className={clsx(
          "grid grid-cols-1 gap-x-8 gap-y-10 list-none xl:gap-x-8 justify-center",
          {
            "grid-cols-2 lg:grid-cols-4":
              itemTemplate?.toLowerCase() === "image focused",
            "grid-cols-1 lg:grid-cols-2":
              itemTemplate?.toLowerCase() === "description focused",
          }
        )}
      >
        {validItems.map(({ item, url }) => (
          <li key={item.id}>
            <Link
              id={`item-card-${item.id}-${category.id}`}
              href={url}
              scroll={false}
              className="block h-full w-full"
              data-testid={applyDataTestId(pom.general.itemName)}
              prefetch={false}
              onClick={() => {
                saveToSessionStorage(
                  SessionStorageKey.LAST_FOCUSED_ITEM_ID,
                  `${item.id}-${category.id}`
                );
              }}
            >
              {itemTemplate?.toLowerCase() === "description focused" && (
                <SingleProductDescriptionFocused
                  merchantName={merchantName}
                  product={item}
                />
              )}
              {itemTemplate?.toLowerCase() === "image focused" && (
                <SingleProductImageFocused product={item} />
              )}
            </Link>
          </li>
        ))}
      </ol>
    </div>
  );
};
